/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { FC, MouseEvent, memo, useState, useEffect, useCallback } from 'react';
import { QSTypography } from '../../../../../../shared/components/atoms';
import { ChatProviderType, ChatType } from './typings';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { getDayFromTimestamp } from '../../../../../../shared/utils/get-day-from-timestamp';
import { MessageTypesEnum } from '../../../typings/message-types';
import { firebaseAuth } from '../../../../../../infra/firebase/init';
import { chatListFamily, phoneDetailsSetter, chatIdsAtom } from '../../../states/chat';
import {
  toggleSelectedChatIdsAtom,
  selectedChatIdsListAtom,
  isAllChatsSelectedAtom,
  excludedChatIdsAtom,
} from '../../../states/chat-list';
import ChatTags from '../../header/components/chat-tags';
import { ChatProfileAvatar } from '../../../atoms/chat-profile-avatar';
import { useLocation } from 'react-router-dom';
import { useCustomNavigate } from '../../../../../../shared/hooks/use-conversation-navigate';
import { conversationPageStateAtom, loadingConversationAtom } from '../../../../../conversations/v1/states';
import { ROUTES } from '../../../../../../shared/constants/routes';
import { urlReplacer } from '../../../../../../shared/utils/url-helpers';
import { useCustomerPhonePermissions } from '../../../../../../shared/hooks/use-permissions';
import { maskPhone } from '../../../utils/mask-phone';
import { validatePhone } from '../../../utils/validate-phone';
import CheckIcon from '@mui/icons-material/Check';
import { chatRowAvatarSize, chatRowAvatarStyles } from '../../../constants';
import ConnectedIntegrationIcon from '../../../../../../shared/icons/ConnectedIntegration';
import { TextEllipsisStyles } from '../../../../../../shared/components/emotion';
import forwardMsgAtom from '../../../../../forward-msg-popup/state';
import { isEmbedAtom } from '../../../../../embed/state';
import useEmbedVisibilitySettings from '../../../../../embed/hooks/use-embed-visibility-settings';
import {
  InsertPhotoOutlined,
  MicNoneOutlined,
  DescriptionOutlined,
  PlaceOutlined,
  PersonOutlineOutlined,
} from '@mui/icons-material';
import { ReactComponent as VideocamOutlined } from '../../../../../../shared/icons/VideoCamOutlined.svg';
import { calculateTime } from '../../../utils/time';
import MessageStatusComponent from './components/MessageStatus';
import MessagePreviewContent from './components/MessagePreviewContent';
import PhoneOutlined from '../../../../../../shared/icons/PhoneOutlined';
import { getCallIConsAndStylesForChatPreview } from '../../../utils/get-call-icons-and-styles-for-chat-preview';
import SLABreachedIcon from '../../../../../../shared/icons/icons-revamp/sla_time';
import { getBGcolor, getNameColor, getPreprocessDisplayText, getReactionPreview, getSlaText } from './utils';
import QSTooltip from '../../../../../../shared/components/molecules/tooltip';
import TimerSimple from '../../../../../../shared/icons/icons-revamp/timer_simple';
import useWabaLevelPermission from '../../../../../../shared/hooks/use-waba-level-permission';
import { Permissions } from '../../../../../../shared/typings/user';
import MessageCloseIcon from '../../../../../../shared/icons/icons-revamp/message_close';

interface ChatRowProps {
  id: string;
  showWaba: boolean;
  forwardMsgView?: boolean;
}

const ChatRow: FC<ChatRowProps> = memo(({ id, showWaba, forwardMsgView }) => {
  const setPhoneDetails = useSetAtom(phoneDetailsSetter);
  const forwardMsgState = useAtomValue(forwardMsgAtom);
  const isEmbed = useAtomValue(isEmbedAtom);
  const { showChatSelection } = useEmbedVisibilitySettings();
  const isMultiSelectAllowed = !isEmbed || showChatSelection;

  const currentUserId = firebaseAuth.currentUser?.uid;

  const setLoadingConversation = useSetAtom(loadingConversationAtom);
  const setConversationPageState = useSetAtom(conversationPageStateAtom);

  const location = useLocation();
  const navigate = useCustomNavigate();
  const { getPhonePermissions } = useCustomerPhonePermissions();
  const hasAccess = getPhonePermissions(currentUserId as string).canRead;

  const toggleSelectedChatIds = useAtomValue(toggleSelectedChatIdsAtom);
  const setToggleSelectedChatIds = useSetAtom(toggleSelectedChatIdsAtom);
  const [selectedChatIdsList, setSelectedChatIdsList] = useAtom(selectedChatIdsListAtom);
  const isAllChatsSelected = useAtomValue(isAllChatsSelectedAtom);
  const excludedChatIds = useAtomValue(excludedChatIdsAtom);

  const [currentSlaTimer, setCurrentSlaTimer] = useState(new Date().getTime());

  let slaInterval: NodeJS.Timeout | null = null;

  const data = useAtomValue(chatListFamily({ id }));

  const {
    id: chatID,
    unreadCount = 0,
    assignedUserId,
    name = '',
    lastMessage,
    chatType,
    chatTypeId,
    selected: openedChat = false,
    imageUrl: profilePicture,
    integrationId = '',
    integrationWabaNumber = '',
    integrationWabaPhoneName = '',
    integrationDisplayName = '',
    tags = [],
    isSlaActive,
    slaDueDate,
    slaBreachLevel,
    lastMessageTime,
    isReminderTriggered,
    providerType,
  } = data || {};

  const { hasPermission } = useWabaLevelPermission();
  const canViewAllReminders = hasPermission(integrationId, Permissions.VIEW_ALL_REMINDERS).hasAccess;

  if (
    lastMessage?.message?.messageType === MessageTypesEnum.TEXT &&
    (lastMessage?.message?.mediaDisable || lastMessage?.message?.textDisable) &&
    lastMessage?.message?.text === ''
  ) {
    lastMessage.message.text =
      'This message may include sensitive customer information and is blocked by your organization owner.';
  }
  const phoneNumber = data?.chatType === ChatType.INDIVIDUAL ? data.phoneNumber : undefined;

  useEffect(() => {
    if (isSlaActive) {
      slaInterval = setInterval(() => {
        setCurrentSlaTimer(new Date().getTime());
      }, 1000);
    }
    return () => {
      if (slaInterval) {
        clearInterval(slaInterval);
      }
    };
  }, [isSlaActive, setCurrentSlaTimer]);

  const slaText = getSlaText(isSlaActive, slaDueDate, currentSlaTimer);
  const hideSlaIndicatorsInForwardFromMediaViewer = !!forwardMsgView && forwardMsgState.showAboveMediaViewer;
  const slaTextIsAvailable = typeof slaText === 'string' && slaText.length > 0;
  const showSlaIcon = slaTextIsAvailable && !hideSlaIndicatorsInForwardFromMediaViewer;
  const showEscalationBadge = typeof slaBreachLevel === 'number' && slaBreachLevel > 0;
  const slaTextForTooltip = (() => {
    if (showEscalationBadge) return `Level ${slaBreachLevel} - ${slaText ?? 'Response delayed'}`;
    if (slaTextIsAvailable) return slaText;
    return '';
  })();
  let maskedPhone = maskPhone(phoneNumber as string, 'X');

  const handleChatExclusion = useAtomCallback(
    useCallback((get, set, chatId: string) => {
      const excluded = get(excludedChatIdsAtom);
      const selected = get(selectedChatIdsListAtom);
      const loadedCount = get(chatIdsAtom).data?.length ?? 0;
      const isExcluded = excluded.includes(chatId);

      const newExcluded = isExcluded ? excluded.filter((eid) => eid !== chatId) : [...excluded, chatId];

      if (loadedCount > 0 && newExcluded.length >= loadedCount) {
        set(isAllChatsSelectedAtom, false);
        set(excludedChatIdsAtom, []);
        set(selectedChatIdsListAtom, []);
      } else {
        set(excludedChatIdsAtom, newExcluded);
        set(selectedChatIdsListAtom, isExcluded ? [...selected, chatId] : selected.filter((sid) => sid !== chatId));
      }
    }, [])
  );

  if (!data) {
    return null;
  }

  const { message, messageOriginType } = lastMessage ?? {};
  const showUnreadUI = !forwardMsgView && unreadCount > 0;

  const chatRowMessageRenderer = ({
    imgSrc,
    text,
    icon,
    textStyle,
  }: {
    imgSrc?: string | undefined;
    icon?: JSX.Element;
    text: string;
    textStyle?: SerializedStyles;
  }) => {
    if ((!imgSrc || imgSrc.length === 0) && !icon && (!text || text.trim()?.length === 0)) {
      return null;
    }

    return (
      <MessagePreviewContent
        message={message}
        messageOriginType={messageOriginType}
        currentUserId={currentUserId}
        showUnreadUI={showUnreadUI}
        imgSrc={imgSrc}
        icon={icon}
        text={text}
        textStyle={textStyle}
        hasAccess={hasAccess}
        phoneNumber={phoneNumber}
      />
    );
  };

  const onChatClick = async () => {
    if (forwardMsgState.showMsgsSelectView && !forwardMsgView) {
      // when forward state is active we will disable switching bw chats
      return;
    }
    setConversationPageState({ isNewPage: false });
    setPhoneDetails({
      phoneNumber: chatTypeId ?? phoneNumber ?? '',
      chatId: id,
      integrationId,
      integrationWabaNumber,
    });
    if (forwardMsgView) {
      updateChatSelection(id);
      return;
    }
    if (providerType === ChatProviderType.INSTAGRAM) {
      const pathname = urlReplacer(
        `${isEmbed ? ROUTES.EMBED.replace('/*', '') : ''}${ROUTES.CONVERSATIONS}/:integrationWabaNumber/:id`,
        [
          [':id', chatTypeId as string],
          [':integrationWabaNumber', integrationWabaNumber],
        ]
      );
      navigate({ pathname, search: location.search });
      return;
    }

    const pathname =
      chatType !== ChatType.WA_GROUP
        ? urlReplacer(
            `${isEmbed ? ROUTES.EMBED.replace('/*', '') : ''}${ROUTES.CONVERSATIONS}/:integrationWabaNumber/:id`,
            [
              [':id', chatTypeId as string],
              [':integrationWabaNumber', integrationWabaNumber],
            ]
          )
        : urlReplacer(`${isEmbed ? ROUTES.EMBED.replace('/*', '') : ''}${ROUTES.CONVERSATIONS}${ROUTES.WA_GROUP}`, [
            [':groupId', data.chatTypeId],
            [':integrationWabaNumber', integrationWabaNumber],
          ]);

    navigate({ pathname, search: location.search });
  };

  const getMessageType = () => {
    let messageType = chatRowMessageRenderer({
      text: '',
    });

    if (message?.messageType === MessageTypesEnum.TEMPLATE) {
      messageType = chatRowMessageRenderer({
        text: message.templateMessage?.body.data[0].text || '',
      });
    }

    if (message?.messageType === MessageTypesEnum.TEXT) {
      let messagetext = message.text;
      if (message.messageTypeWeb === MessageTypesEnum.FLOW_RESPONSE) {
        messagetext = JSON.stringify(message?.userResponse?.payload || '');
      }
      messageType = chatRowMessageRenderer({
        text: messagetext || '',
      });
    }

    if (message?.messageType === MessageTypesEnum.INTERACTIVE) {
      messageType = chatRowMessageRenderer({
        text: message.interactiveMessage?.body?.text || 'Interactive message',
      });
    }

    if (message?.messageType === MessageTypesEnum.IMAGE) {
      messageType = chatRowMessageRenderer({
        icon: (
          <InsertPhotoOutlined
            sx={{
              height: 16,
              width: 16,
              color: '#616161',
            }}
          />
        ),
        text: 'Image',
      });
    }

    if (message?.messageType === MessageTypesEnum.VIDEO) {
      messageType = chatRowMessageRenderer({
        icon: <VideocamOutlined />,
        text: 'Video' + (message?.duration ? `${' '}(${calculateTime(message.duration) || '--:--'})` : ''),
      });
    }

    if (message?.messageType === MessageTypesEnum.DOCUMENT) {
      messageType = chatRowMessageRenderer({
        icon: (
          <DescriptionOutlined
            sx={{
              height: 16,
              width: 16,
              color: '#616161',
            }}
          />
        ),
        text: message?.fileName ?? 'Document',
      });
    }

    if (message?.messageType === MessageTypesEnum.AUDIO) {
      messageType = chatRowMessageRenderer({
        icon: (
          <MicNoneOutlined
            sx={{
              height: 16,
              width: 16,
              color: '#616161',
            }}
          />
        ),
        text: 'Audio' + (message?.duration ? `${' '}(${calculateTime(message.duration) || '--:--'})` : ''),
      });
    }

    if (message?.messageType === MessageTypesEnum.SYSTEM || message?.messageType === MessageTypesEnum.SYSTEM_LOG) {
      messageType = chatRowMessageRenderer({
        text: message.text,
      });
    }

    if (message?.messageType === MessageTypesEnum.CONTACTS) {
      messageType = chatRowMessageRenderer({
        icon: (
          <PersonOutlineOutlined
            sx={{
              height: 16,
              width: 16,
              color: '#616161',
            }}
          />
        ),
        text: 'Contacts',
      });
    }

    if (message?.messageType === MessageTypesEnum.BUTTON) {
      messageType = chatRowMessageRenderer({
        text: message.text,
      });
    }

    if (message?.messageType === MessageTypesEnum.LOCATION) {
      messageType = chatRowMessageRenderer({
        icon: (
          <PlaceOutlined
            sx={{
              height: 16,
              width: 16,
              color: '#616161',
            }}
          />
        ),
        text: 'Location',
      });
    }

    if (message?.messageType === MessageTypesEnum.CALL) {
      const { icon, title, titleColor, iconColor } = getCallIConsAndStylesForChatPreview({
        callMessage: message,
        size: 16,
      });
      messageType = chatRowMessageRenderer({
        icon: icon ? (
          <Box
            sx={{
              color: iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        ) : (
          <PhoneOutlined
            css={css`
              height: 16px;
              width: 16px;
              color: #616161;
            `}
          />
        ),
        text:
          title + (message?.durationInSeconds ? `${' '}(${calculateTime(message.durationInSeconds) || '--:--'})` : ''),
        textStyle: css`
          color: ${titleColor};
          font-size: 14px;
          line-height: 21px;
        `,
      });
    }

    if (message?.messageType === MessageTypesEnum.UNSUPPORTED) {
      messageType = chatRowMessageRenderer({
        icon: <MessageCloseIcon size={16} color="#888888" />,
        text: 'Unsupported message',
      });
    }

    return messageType;
  };

  const messageType = getMessageType();
  const preProcessedText = getPreprocessDisplayText({ message });

  const isChatSelected = isAllChatsSelected ? !excludedChatIds.includes(id) : selectedChatIdsList.includes(id);

  const updateChatSelection = (chatId: string) => {
    if (isAllChatsSelected) {
      handleChatExclusion(chatId);
      return;
    }
    const isIdPresent = selectedChatIdsList.includes(chatId);
    if (isIdPresent) {
      setSelectedChatIdsList(selectedChatIdsList.filter((idPresent) => chatId !== idPresent));
    } else {
      setSelectedChatIdsList([...selectedChatIdsList, chatId]);
    }
  };

  const nameOrPhone = name ? name.trim() : '';
  const isPhone = validatePhone(nameOrPhone);

  const shouldShowWabaIntegration =
    showWaba &&
    chatType &&
    [ChatType.INDIVIDUAL, ChatType.WA_GROUP].includes(chatType) &&
    ((typeof integrationDisplayName === 'string' && integrationDisplayName.length > 0) ||
      (typeof integrationWabaPhoneName === 'string' && integrationWabaPhoneName.length > 0));

  const showCheckboxInPlaceOfAvatar = (forwardMsgView || toggleSelectedChatIds) && !!chatID;

  const handleCheckboxClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!toggleSelectedChatIds && !forwardMsgView) {
      setToggleSelectedChatIds(true);
    }
    updateChatSelection(chatID!);
  };

  return (
    <ButtonBase<'div'>
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: stretch;
        padding: 12px 16px;
        row-gap: 8px;
        width: 100%;
        background-color: ${getBGcolor({
          forwardMsgView,
          forwardMsgState,
          openedChat,
          selectedChatIdsList,
          chatID,
          showUnreadUI,
          isAllChatsSelected,
          excludedChatIds,
        })};
        cursor: pointer;
        overflow: hidden;
        min-height: calc(${chatRowAvatarSize} + 2 * 16px);

        ${!openedChat &&
        !(isAllChatsSelected ? !excludedChatIds.includes(chatID ?? '') : selectedChatIdsList?.includes(chatID ?? '')) &&
        css`
          &:hover {
            background: rgba(248, 248, 248, 1);
          }
        `}
      `}
      onClick={onChatClick}
      component="div"
      className="chat-list-btn"
    >
      <Box
        css={css`
          width: 100%;
          display: flex;
          gap: 12px;
        `}
      >
        <QSTooltip
          title={isMultiSelectAllowed && !forwardMsgView ? (isChatSelected ? 'Deselect' : 'Select') : ''}
          placement="top"
        >
          <div
            css={css`
              position: relative;
              width: ${chatRowAvatarSize}px;
              height: ${chatRowAvatarSize}px;
              min-width: ${chatRowAvatarSize}px;
              min-height: ${chatRowAvatarSize}px;
              border-radius: 50%;
              overflow: visible;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;

              .avatar {
                display: flex;
                width: 100%;
                height: 100%;
                align-items: center;
                justify-content: center;
              }

              .checkbox {
                display: none;
                position: absolute;
                inset: 0;
                align-items: center;
                justify-content: center;
              }

              ${!forwardMsgView &&
              isMultiSelectAllowed &&
              css`
                &:hover .avatar {
                  display: none;
                }
                &:hover .checkbox {
                  display: flex;
                }
              `}

              ${showCheckboxInPlaceOfAvatar &&
              css`
                .avatar {
                  display: none;
                }
                .checkbox {
                  display: flex;
                }
              `}
            `}
          >
            <div className="avatar">
              <ChatProfileAvatar
                chatType={chatType}
                name={name}
                phoneNumber={phoneNumber}
                profilePicture={profilePicture}
                providerType={providerType}
                showProviderIcon={true}
              />
            </div>

            <div className="checkbox" onClick={handleCheckboxClick}>
              <div
                css={css(
                  chatRowAvatarStyles,
                  css`
                    border: 1px solid #e0e0e9;
                    background: #ffffff;
                    flex-shrink: 0;
                  `
                )}
              >
                {isChatSelected ? (
                  <div
                    css={css`
                      width: 18px;
                      height: 18px;
                      border-radius: 4px;
                      border: 2px solid #3f906d;
                      background: #3f906d;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    `}
                  >
                    <CheckIcon
                      css={css`
                        color: #ffffff;
                        font-size: 14px;
                      `}
                    />
                  </div>
                ) : (
                  <div
                    css={css`
                      width: 18px;
                      height: 18px;
                      border-radius: 4px;
                      border: 2px solid #7a7a7a;
                      background: transparent;
                    `}
                  />
                )}
              </div>
            </div>
          </div>
        </QSTooltip>

        <Box
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            flex: 1;
            min-width: 0px;
            min-height: 0px;
            width: 100%;
          `}
        >
          {((typeof name === 'string' && name.trim().length > 0) || showSlaIcon || showUnreadUI) && (
            <Box
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
              `}
            >
              {typeof name === 'string' && name.trim().length > 0 && (
                <QSTypography
                  css={css`
                    font-size: 16px;
                    font-weight: ${showUnreadUI || (!isPhone && forwardMsgView) ? '600' : '500'};
                    color: ${getNameColor({ showUnreadUI, forwardMsgView })};
                    flex: 1;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {isPhone ? (hasAccess ? nameOrPhone : maskedPhone) : nameOrPhone}
                </QSTypography>
              )}
              {isReminderTriggered && canViewAllReminders && !hideSlaIndicatorsInForwardFromMediaViewer && (
                <Box
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    padding-right: ${showSlaIcon || showUnreadUI ? '8px' : '0px'};
                  `}
                >
                  <TimerSimple size={18} />
                </Box>
              )}
              {(showSlaIcon || showUnreadUI) && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    column-gap: 8px;
                  `}
                >
                  {showSlaIcon && (
                    <QSTooltip title={slaTextForTooltip}>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          column-gap: 2px;
                          ${showEscalationBadge
                            ? `
                              background: rgba(239, 83, 80, 0.10);
                              border-radius: 4px;
                              padding: 2px 4px;
                            `
                            : ''}
                        `}
                      >
                        <SLABreachedIcon size={18} color="#EF5350" />
                        {showEscalationBadge && (
                          <QSTypography
                            css={css`
                              font-size: 10px;
                              font-weight: 600;
                              color: #ef5350;
                              white-space: nowrap;
                            `}
                          >
                            {slaBreachLevel}
                          </QSTypography>
                        )}
                      </div>
                    </QSTooltip>
                  )}
                  {showUnreadUI && (
                    <Box
                      css={css`
                        min-width: 20px;
                        min-height: 20px;
                        display: flex;
                        align-items: center;
                        background-color: #3f906d;
                        justify-content: center;
                        padding: 2px;
                        border-radius: ${unreadCount?.toString?.()?.length > 2 ? '10px' : '50%'};
                      `}
                    >
                      <QSTypography
                        css={css`
                          font-size: 10px;
                          font-weight: 700;
                          color: white;
                          white-space: nowrap;
                          padding: ${unreadCount?.toString?.()?.length > 2 ? '0px 4px' : '0px'};
                        `}
                        textAlign="center"
                      >
                        {unreadCount}
                      </QSTypography>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          )}
          {forwardMsgView && !isPhone && (
            <Box
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
              `}
            >
              <QSTypography
                css={css`
                  font-size: 16px;
                  font-weight: 400;
                  color: #6d6d6d;
                  flex: 1;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {hasAccess ? phoneNumber : maskedPhone}
              </QSTypography>
            </Box>
          )}
          {shouldShowWabaIntegration ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: flex-start;
                column-gap: 6px;
                width: 100%;
                margin: 4px 0px;
              `}
            >
              <ConnectedIntegrationIcon color="rgba(122, 122, 122, 1)" />
              <QSTypography
                css={css(
                  css`
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: normal;
                    text-align: left;
                    color: rgba(122, 122, 122, 1);
                  `,
                  TextEllipsisStyles
                )}
              >
                {integrationDisplayName || integrationWabaPhoneName}
              </QSTypography>
            </div>
          ) : null}
          {!forwardMsgView && (!!messageType || !!lastMessageTime) && (
            <Box
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                gap: 4px;
              `}
            >
              <Box
                css={css`
                  display: flex;
                  align-items: center;
                  column-gap: 4px;
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                `}
              >
                <MessageStatusComponent
                  lastMessage={lastMessage}
                  getReactionPreview={() => getReactionPreview({ message, messageOriginType, currentUserId })}
                />
                {!!messageType && (
                  <Box
                    css={css`
                      flex: 1;
                      min-width: 0;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    <Tooltip
                      enterDelay={1000}
                      enterNextDelay={1000}
                      title={preProcessedText ?? ''}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxHeight: '300px',
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      {messageType}
                    </Tooltip>
                  </Box>
                )}
              </Box>
              {!!lastMessageTime && (
                <Box
                  css={css`
                    flex-shrink: 0;
                    min-width: fit-content;
                  `}
                >
                  <QSTypography
                    css={css`
                      font-size: 10px;
                      font-weight: 600;
                      white-space: nowrap;
                      color: rgb(155, 155, 155);
                    `}
                  >
                    {getDayFromTimestamp(lastMessageTime)}
                  </QSTypography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {!forwardMsgView ? <ChatTags tags={tags} /> : null}
    </ButtonBase>
  );
});

export default ChatRow;
